import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ChevronRight, AlertTriangle, TrendingUp, UserCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { RiskBadge } from './RiskBadge';
import { cn } from '../../lib/utils';

export function AIInsightCard({ 
  title, 
  reason, 
  confidence, 
  actions = [],
  type = 'alert',
  onAction,
  index = 0
}) {
  const getTypeIcon = () => {
    switch (type) {
      case 'risk':
        return AlertTriangle;
      case 'improvement':
        return TrendingUp;
      case 'compliance':
        return UserCheck;
      default:
        return Sparkles;
    }
  };
  
  const getTypeBg = () => {
    switch (type) {
      case 'risk':
        return 'bg-primary-light';
      case 'improvement':
        return 'bg-success-light';
      case 'compliance':
        return 'bg-info-light';
      default:
        return 'bg-accent';
    }
  };
  
  const getTypeColor = () => {
    switch (type) {
      case 'risk':
        return 'text-primary';
      case 'improvement':
        return 'text-success';
      case 'compliance':
        return 'text-info';
      default:
        return 'text-accent-foreground';
    }
  };

  const TypeIcon = getTypeIcon();

  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      className="p-4 rounded-xl border border-border bg-card hover:shadow-elevated transition-shadow duration-200"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', getTypeBg())}>
          <TypeIcon className={cn('w-4 h-4', getTypeColor())} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground mb-1">{title}</h4>
          <p className="text-xs text-muted-foreground line-clamp-2">{reason}</p>
        </div>
      </div>
      
      {confidence && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${confidence}%` }}
            />
          </div>
          <span className="text-xs text-muted-foreground">{confidence}%</span>
        </div>
      )}
      
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant="outline"
              size="sm"
              className="text-xs h-7"
              onClick={() => onAction?.(action)}
            >
              {action}
            </Button>
          ))}
        </div>
      )}
    </motion.div>
  );
}

export function AIInsightPanel({ title, insights = [], className }) {
  return (
    <div className={cn('bg-card rounded-xl border border-border', className)}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-display font-semibold text-foreground">{title}</h3>
        </div>
        <Button variant="ghost" size="sm" className="text-xs">
          View all <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin">
        {insights.map((insight, idx) => (
          <AIInsightCard key={idx} {...insight} index={idx} />
        ))}
      </div>
    </div>
  );
}
